import React, { useEffect } from 'react';
import styled from 'styled-components';
import { ChecklistContainer,
        Checklist, 
        ChecklistDivider, 
        ChecklistItemStyle, 
        SlideSection, 
        SectionHeader } from '../styles';
import { useTranslation } from 'react-i18next';
import { usePresentation } from '@context/presentation';
import { useSlideData } from '@context/slide-data';
import { SectionLabel, GridList } from '../shared';
import { selectInfoItem, setStepsReviewed } from '@actions/creators';
import { updateInfoItem, addStepReviewed } from '@services/sse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { BROWSER_STORAGE_KEYS, UNAVAILABLE_CS_FIRM } from '@constants';
import { retrieveSavedData, setBrowserStorageItem, updateSavedSteps } from '@utils/browserStorage';

const AdditionalInfoStyle = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
`;

export const AdditionalInfo = () => {
    const { t } = useTranslation();
    const { client, clientViewing } = usePresentation();
    const { firm } = client;
    const { checkboxKey } = BROWSER_STORAGE_KEYS;
    const { data, slideState, sectionsVisible, dispatch, desktop } = useSlideData();
    const { selectedSection, infoItem, stepsReviewed } = slideState;
    const { id } = data;
    const firmToRender = firm === UNAVAILABLE_CS_FIRM ? '(Insert Firm Here)' : firm;

    const reviewedInformation = stepsReviewed[id];

    useEffect(() => {
        retrieveSavedData([checkboxKey], dispatch);
    }, []);

    const selectedScriptSection = selectedSection ? selectedSection[data.id] : null;
    const gridOptions = [
        t('additional_info.debt_free'), 
        `${firmToRender} ${t('additional_info.firm_negotiates')}`,
        t('additional_info.reduced_payment'), 
        t('additional_info.settled_fees') 
    ];

    const firstChecklistOptions = [
        t('additional_info.important.credit_score'),
        t('additional_info.important.debt_collection'),
        t('additional_info.important.additional_fees')
    ];

    const secondChecklistOptions = [
        t('additional_info.important.savings_taxable'),
        t('additional_info.important.prevents_cc_usage'),
        t('additional_info.important.results_not_guaranteed'),
    ];

    const handleImportantInfo = (text) => {
        if (!clientViewing) {
            dispatch(selectInfoItem(text));
            updateInfoItem({ item: text });
            setBrowserStorageItem(checkboxKey, text);

            // Add the checklist item to steps reviewed for this slide
            dispatch(setStepsReviewed(id, text));
            addStepReviewed({ slideId: id, step: text });
            updateSavedSteps(id, text);
        }
    };

    const CheckListItem = ({ text, id }) => (
        <ChecklistItemStyle
            desktop={desktop}
            selected={infoItem === text}
            covered={reviewedInformation.includes(text)}
            onClick={() => handleImportantInfo(text)}>
                <FontAwesomeIcon icon={faCheckCircle} />
                <label htmlFor={`checklist-${id}`}>
                    {text}
                </label>
        </ChecklistItemStyle>
    );

    return (
        <AdditionalInfoStyle>
            <SlideSection 
                visible={sectionsVisible}
                selected={selectedScriptSection === 1}>
                    <SectionLabel section={1} />
                    <SectionHeader>
                        {t('additional_info.benefits')}
                    </SectionHeader>
                    <GridList options={gridOptions} />
            </SlideSection>
            <SlideSection
              height={desktop ? '50%' : '100%'}
              visible={sectionsVisible}
              selected={selectedScriptSection === 2}>
                    <SectionLabel section={2} />
                    <SectionHeader>
                        {t('additional_info.important.header')}
                    </SectionHeader>
                    <ChecklistContainer desktop={desktop}>
                        <Checklist desktop={desktop}>
                            {
                                firstChecklistOptions.map((option, index) => (
                                    <CheckListItem key={index} id={index} text={option} />
                                ))
                            }
                        </Checklist>
                        {desktop && <ChecklistDivider />}
                        <Checklist desktop={desktop}>
                            {
                                secondChecklistOptions.map((option, index) => (
                                    <CheckListItem key={index} id={index} text={option} />
                                ))
                            }
                        </Checklist>
                    </ChecklistContainer>
            </SlideSection>
            <SlideSection
              visible={sectionsVisible}
              selected={selectedScriptSection === 3} >
                   <SectionLabel section={3} />
            </SlideSection>
        </AdditionalInfoStyle>
    );
};

export default AdditionalInfo;
